'use client';

/**
 * AddressAutocomplete — Google Places typeahead address input.
 *
 * Lazy-loads the Google Maps JS API on first focus (NOT in the initial
 * bundle) using `@googlemaps/js-api-loader`.
 *
 * Resolution order — defense-in-depth against partial GCP enablement:
 *   1) NEW API: `AutocompleteSuggestion.fetchAutocompleteSuggestions` +
 *      `Place.fetchFields()`. Cheapest, modern, what Google now recommends.
 *   2) LEGACY API: `AutocompleteService.getPlacePredictions` +
 *      `PlacesService.getDetails`. Used as fallback when the new API is
 *      not enabled in the GCP project (HTTP 403 from
 *      `places.googleapis.com/$rpc/.../AutocompletePlaces`) or when
 *      `AutocompleteSuggestion` is missing on the loaded library.
 *   3) PLAIN INPUT: when both endpoints fail, render a free-text input
 *      with a "validation unavailable" hint (same UI as the no-API-key
 *      fallback). The parent decides what to do with the raw text.
 *
 * Once the component flips to LEGACY for this instance it stays on
 * LEGACY for the rest of its lifetime — we don't retry the new API
 * until the component remounts. Same rule for the plain-input fallback.
 *
 * Forces the user to PICK a suggestion before the parent can read a
 * valid value — the parent owns the "submit disabled" state via
 * `onChange(resolved)` where `resolved` is non-null only after a
 * selection.
 *
 * Used by AddServiceModal.tsx for pickup + dropoff. Generic enough to
 * be reused for any address picker.
 */

import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import { importLibrary, setOptions } from '@googlemaps/js-api-loader';
import styles from './AddressAutocomplete.module.css';

/* ─────────────── TYPES ─────────────── */

export interface ResolvedAddress {
  /** Google `place_id` — empty string when fallback (no API key). */
  placeId: string;
  /** Canonical display string from Google or raw text in fallback. */
  formattedAddress: string;
}

export interface AddressAutocompleteProps {
  /** Field label (rendered outside the input by the parent — optional override here). */
  label?: string;
  /** Input id — auto-generated when not provided. */
  id?: string;
  /** Placeholder shown inside the input. */
  placeholder?: string;
  /**
   * ISO-3166-1 alpha-2 country code (or array) to bias suggestions.
   * Google requires lowercase. Pass `undefined` for no restriction.
   */
  countryCode?: string | string[];
  /** Currently resolved value (only set after a suggestion is picked). */
  value: ResolvedAddress | null;
  /** Fired whenever the resolution changes (null = cleared / not picked yet). */
  onChange: (resolved: ResolvedAddress | null) => void;
  /** Optional warn copy when the user has typed text but not picked a suggestion. */
  warnText?: string;
  /** Inline "fallback active" copy when the API key is missing. */
  fallbackText?: string;
  /** Hide the resolved-address confirmation chip (parent renders its own). */
  hideConfirmation?: boolean;
}

/* ─────────────── INTERNAL ─────────────── */

interface Suggestion {
  placeId: string;
  primary: string;
  secondary: string;
}

/**
 * setOptions() must be called exactly once with the API key before the
 * first importLibrary() call. We guard with a module-scope flag and a
 * cached promise so concurrent mounts don't race.
 */
let optionsConfigured = false;
let placesImportPromise: Promise<google.maps.PlacesLibrary> | null = null;

function ensurePlacesLibrary(apiKey: string): Promise<google.maps.PlacesLibrary> {
  if (!optionsConfigured) {
    setOptions({ key: apiKey, v: 'weekly' });
    optionsConfigured = true;
  }
  if (!placesImportPromise) {
    placesImportPromise = importLibrary('places');
  }
  return placesImportPromise;
}

type AutocompleteService = google.maps.places.AutocompleteService;
type AutocompleteSessionToken = google.maps.places.AutocompleteSessionToken;
type PlacesService = google.maps.places.PlacesService;

/**
 * Heuristic: do we treat this error as "new Places API is not
 * enabled in the GCP project"? Triggers the legacy fallback.
 *
 * Matches:
 *   - Google's literal "Places API (New) has not been used" string.
 *   - REQUEST_DENIED / PERMISSION_DENIED gRPC status codes.
 *   - The HTTP 403 status appearing in the error message.
 *   - NOT_FOUND (some early-rollout regions return this).
 */
const NEW_API_DISABLED_RX =
  /Places API \(New\) has not been used|REQUEST_DENIED|PERMISSION_DENIED|403|NOT_FOUND/i;

function isNewApiDisabledError(err: unknown): boolean {
  if (err instanceof Error) return NEW_API_DISABLED_RX.test(err.message);
  if (typeof err === 'string') return NEW_API_DISABLED_RX.test(err);
  if (err && typeof err === 'object' && 'message' in err) {
    const msg = (err as { message: unknown }).message;
    if (typeof msg === 'string') return NEW_API_DISABLED_RX.test(msg);
  }
  return false;
}

/**
 * Google logs two `console.warn` lines from `places.js` at module load
 * time about AutocompleteService/PlacesService being unavailable to new
 * customers. We only ever hit the legacy code path when the new API is
 * not enabled — so for that user the warnings are noise, not signal.
 *
 * Install the shim LAZILY (only after the first legacy fallback in this
 * tab) and FILTER instead of replacing, so other warnings still surface.
 */
let legacyWarnShimInstalled = false;
const LEGACY_WARN_FILTERS = [
  'google.maps.places.AutocompleteService is not available to new customers',
  'google.maps.places.PlacesService is not available to new customers',
];

function installLegacyWarnShim(): void {
  if (legacyWarnShimInstalled) return;
  if (typeof console === 'undefined' || typeof console.warn !== 'function') {
    return;
  }
  legacyWarnShimInstalled = true;
  const original = console.warn.bind(console);
  console.warn = (...args: unknown[]): void => {
    const first = args[0];
    if (typeof first === 'string') {
      for (const filter of LEGACY_WARN_FILTERS) {
        if (first.includes(filter)) return;
      }
    }
    original(...args);
  };
}

/* ─────────────── COMPONENT ─────────────── */

export default function AddressAutocomplete({
  label,
  id,
  placeholder = 'Search address…',
  countryCode,
  value,
  onChange,
  warnText,
  fallbackText = 'Address validation unavailable',
  hideConfirmation = false,
}: AddressAutocompleteProps) {
  const apiKey = process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY ?? '';
  const useFallback = !apiKey;
  const autoId = useId();
  const inputId = id ?? `addr-${autoId}`;

  const [query, setQuery] = useState<string>(value?.formattedAddress ?? '');
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  /**
   * Set to true when BOTH the new API and the legacy API fail at runtime
   * for this component instance — flips the UI to plain-text input mode
   * without unsetting the `useFallback` (no-API-key) flag.
   */
  const [bothFailed, setBothFailed] = useState(false);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [highlight, setHighlight] = useState<number>(-1);
  const [picked, setPicked] = useState<ResolvedAddress | null>(value);

  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  /**
   * Sticky per-instance flag: once we flip to legacy, never retry the
   * new API for this component again (avoids re-throwing 403 on every
   * keystroke and avoids double-billing).
   */
  const useLegacyRef = useRef<boolean>(false);
  const autocompleteServiceRef = useRef<AutocompleteService | null>(null);
  const placesServiceRef = useRef<PlacesService | null>(null);
  const placesLibRef = useRef<google.maps.PlacesLibrary | null>(null);
  const sessionTokenRef = useRef<AutocompleteSessionToken | null>(null);
  const debounceTimerRef = useRef<number | null>(null);
  const dummyDivRef = useRef<HTMLDivElement | null>(null);
  /** Latest request id — guards against out-of-order autocomplete callbacks. */
  const requestIdRef = useRef<number>(0);

  /* The combined "render plain input" condition for the UI. */
  const renderPlain = useFallback || bothFailed;

  /* Reset internal picked state when parent clears value. */
  useEffect(() => {
    if (!value) {
      setPicked(null);
      return;
    }
    setPicked(value);
    setQuery(value.formattedAddress);
  }, [value]);

  /* Build component restrictions for Google biasing. */
  const componentRestrictions = useMemo<
    google.maps.places.ComponentRestrictions | undefined
  >(() => {
    if (!countryCode) return undefined;
    const codes = Array.isArray(countryCode)
      ? countryCode
      : [countryCode];
    const cleaned = codes
      .map((c) => c.trim().toLowerCase())
      .filter((c) => c.length === 2);
    if (cleaned.length === 0) return undefined;
    return { country: cleaned };
  }, [countryCode]);

  /**
   * Region codes for the NEW API — same source as `componentRestrictions`
   * but transformed for `AutocompleteRequest.includedRegionCodes` (CLDR
   * 2-letter codes; case-insensitive in practice).
   */
  const includedRegionCodes = useMemo<string[] | undefined>(() => {
    if (!countryCode) return undefined;
    const codes = Array.isArray(countryCode)
      ? countryCode
      : [countryCode];
    const cleaned = codes
      .map((c) => c.trim().toUpperCase())
      .filter((c) => c.length === 2);
    return cleaned.length === 0 ? undefined : cleaned;
  }, [countryCode]);

  /* Lazy-load Google Maps API on first focus / first keystroke. */
  const ensureLoaded = useCallback(async (): Promise<boolean> => {
    if (renderPlain) return false;
    // Already initialised for the current path? Bail early.
    if (useLegacyRef.current) {
      if (autocompleteServiceRef.current && placesServiceRef.current) return true;
    } else if (placesLibRef.current) {
      return true;
    }
    if (loadFailed) return false;
    try {
      setLoading(true);
      const places = await ensurePlacesLibrary(apiKey);
      placesLibRef.current = places;

      // If the loaded library doesn't expose the new API surface,
      // flip to legacy immediately (no need to make a live RPC).
      if (typeof places.AutocompleteSuggestion === 'undefined') {
        useLegacyRef.current = true;
      }

      // Always initialise a session token — both APIs accept it.
      sessionTokenRef.current = new places.AutocompleteSessionToken();

      // Lazy-init the legacy services only when we know we'll need them.
      if (useLegacyRef.current) {
        installLegacyWarnShim();
        autocompleteServiceRef.current = new places.AutocompleteService();
        // PlacesService needs an HTMLDivElement OR a google.maps.Map.
        // A detached div is sufficient — Google uses it only for branding.
        if (!dummyDivRef.current) {
          dummyDivRef.current = document.createElement('div');
        }
        placesServiceRef.current = new places.PlacesService(
          dummyDivRef.current,
        );
      }
      return true;
    } catch (err) {
      console.error('[AddressAutocomplete] Failed to load Google Maps API', err);
      setLoadFailed(true);
      return false;
    } finally {
      setLoading(false);
    }
  }, [apiKey, renderPlain, loadFailed]);

  /* Outside click closes the dropdown. */
  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (!open) return;
      const target = e.target as Node;
      if (wrapperRef.current && !wrapperRef.current.contains(target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  /* Cleanup pending debounce timer on unmount. */
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current !== null) {
        window.clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
    };
  }, []);

  /**
   * Lazy-init the LEGACY services after the new API has failed. The
   * library is already in `placesLibRef`; we just need to spin up the
   * legacy service objects, install the warn shim, and rotate to a
   * fresh session token (the new API may have consumed the previous one).
   */
  const ensureLegacyServices = useCallback((): boolean => {
    const places = placesLibRef.current;
    if (!places) return false;
    installLegacyWarnShim();
    if (!autocompleteServiceRef.current) {
      autocompleteServiceRef.current = new places.AutocompleteService();
    }
    if (!placesServiceRef.current) {
      if (!dummyDivRef.current) {
        dummyDivRef.current = document.createElement('div');
      }
      placesServiceRef.current = new places.PlacesService(dummyDivRef.current);
    }
    if (!sessionTokenRef.current) {
      sessionTokenRef.current = new places.AutocompleteSessionToken();
    }
    return true;
  }, []);

  /* ─── LEGACY API: getPlacePredictions ─── */
  const fetchSuggestionsLegacy = useCallback(
    (input: string, myId: number): void => {
      const service = autocompleteServiceRef.current;
      if (!service) {
        // Could not even spin up legacy services → plain input.
        setBothFailed(true);
        setSuggestions([]);
        setOpen(false);
        return;
      }
      service.getPlacePredictions(
        {
          input,
          sessionToken: sessionTokenRef.current ?? undefined,
          // Cover hotels, airports, generic establishments, raw addresses.
          types: ['establishment', 'geocode'],
          componentRestrictions,
        },
        (predictions, status) => {
          // Ignore stale callbacks (user typed something newer).
          if (myId !== requestIdRef.current) return;
          if (
            status === google.maps.places.PlacesServiceStatus.OK &&
            predictions
          ) {
            setSuggestions(
              predictions.slice(0, 6).map((p) => ({
                placeId: p.place_id,
                primary: p.structured_formatting?.main_text ?? p.description,
                secondary: p.structured_formatting?.secondary_text ?? '',
              })),
            );
            setHighlight(-1);
          } else if (
            status === google.maps.places.PlacesServiceStatus.REQUEST_DENIED ||
            status === google.maps.places.PlacesServiceStatus.UNKNOWN_ERROR
          ) {
            // Legacy also failed → plain input.
            setBothFailed(true);
            setSuggestions([]);
            setOpen(false);
          } else {
            // ZERO_RESULTS / INVALID_REQUEST / over_query_limit — just
            // show no suggestions, keep the user typing.
            setSuggestions([]);
          }
        },
      );
    },
    [componentRestrictions],
  );

  /* ─── NEW API: AutocompleteSuggestion.fetchAutocompleteSuggestions ─── */
  const fetchSuggestionsNew = useCallback(
    async (input: string, myId: number): Promise<void> => {
      const places = placesLibRef.current;
      if (!places || typeof places.AutocompleteSuggestion === 'undefined') {
        // Should not happen (ensureLoaded would have flipped useLegacy
        // already) but be defensive.
        useLegacyRef.current = true;
        ensureLegacyServices();
        fetchSuggestionsLegacy(input, myId);
        return;
      }
      try {
        const request: google.maps.places.AutocompleteRequest = {
          input,
          sessionToken: sessionTokenRef.current ?? undefined,
          ...(includedRegionCodes ? { includedRegionCodes } : {}),
        };
        const { suggestions: results } =
          await places.AutocompleteSuggestion.fetchAutocompleteSuggestions(
            request,
          );
        if (myId !== requestIdRef.current) return;
        const mapped: Suggestion[] = [];
        for (const s of results.slice(0, 6)) {
          const pred = s.placePrediction;
          if (!pred) continue;
          mapped.push({
            placeId: pred.placeId,
            primary: pred.mainText?.text ?? pred.text.text,
            secondary: pred.secondaryText?.text ?? '',
          });
        }
        setSuggestions(mapped);
        setHighlight(-1);
      } catch (err) {
        if (myId !== requestIdRef.current) return;
        if (isNewApiDisabledError(err)) {
          // Flip to legacy for the rest of this component's lifetime
          // and immediately re-execute the same request.
          console.warn(
            '[AddressAutocomplete] New Places API unavailable, falling back to legacy',
            err,
          );
          useLegacyRef.current = true;
          if (ensureLegacyServices()) {
            fetchSuggestionsLegacy(input, myId);
          } else {
            setBothFailed(true);
            setSuggestions([]);
            setOpen(false);
          }
          return;
        }
        // Unknown error — log and clear suggestions, keep state stable.
        console.error(
          '[AddressAutocomplete] fetchAutocompleteSuggestions failed',
          err,
        );
        setSuggestions([]);
      }
    },
    [
      includedRegionCodes,
      ensureLegacyServices,
      fetchSuggestionsLegacy,
    ],
  );

  /* Dispatcher: pick the right path for the current request. */
  const fetchSuggestions = useCallback(
    (input: string): void => {
      if (renderPlain) return;
      const trimmed = input.trim();
      if (trimmed.length < 2) {
        setSuggestions([]);
        return;
      }
      const myId = ++requestIdRef.current;
      if (useLegacyRef.current) {
        ensureLegacyServices();
        fetchSuggestionsLegacy(trimmed, myId);
      } else {
        void fetchSuggestionsNew(trimmed, myId);
      }
    },
    [
      renderPlain,
      ensureLegacyServices,
      fetchSuggestionsLegacy,
      fetchSuggestionsNew,
    ],
  );

  const handleQueryChange = useCallback(
    (next: string) => {
      setQuery(next);
      // Any edit invalidates a previous pick.
      if (picked) {
        setPicked(null);
        onChange(null);
      }
      if (renderPlain) {
        // Fallback: parent treats raw text as the resolved value.
        // Empty string = null so the parent can still gate validation.
        if (next.trim().length === 0) onChange(null);
        else onChange({ placeId: '', formattedAddress: next.trim() });
        return;
      }
      setOpen(true);
      if (debounceTimerRef.current !== null) {
        window.clearTimeout(debounceTimerRef.current);
      }
      debounceTimerRef.current = window.setTimeout(() => {
        debounceTimerRef.current = null;
        void ensureLoaded().then((ok) => {
          if (ok) fetchSuggestions(next);
        });
      }, 220);
    },
    [picked, onChange, renderPlain, ensureLoaded, fetchSuggestions],
  );

  const handleFocus = useCallback(() => {
    if (renderPlain) return;
    void ensureLoaded();
    if (query.trim().length >= 2) setOpen(true);
  }, [renderPlain, ensureLoaded, query]);

  /* ─── SELECTION: new API path ─── */
  const selectViaNewApi = useCallback(
    async (s: Suggestion): Promise<ResolvedAddress | null> => {
      const places = placesLibRef.current;
      if (!places) return null;
      try {
        const place = new places.Place({ id: s.placeId });
        await place.fetchFields({
          fields: ['id', 'displayName', 'formattedAddress'],
        });
        const formatted =
          place.formattedAddress ||
          [place.displayName, s.secondary].filter(Boolean).join(', ') ||
          s.primary;
        // For establishments (hotel, airport) prefix the name so the
        // user sees "Sheraton Santiago, Av. ..." rather than the raw
        // street address — this is the Uber/Airbnb feel.
        const display =
          place.displayName &&
          !formatted.toLowerCase().startsWith(place.displayName.toLowerCase())
            ? `${place.displayName}, ${formatted}`
            : formatted;
        return {
          placeId: place.id ?? s.placeId,
          formattedAddress: display,
        };
      } catch (err) {
        if (isNewApiDisabledError(err)) {
          useLegacyRef.current = true;
          ensureLegacyServices();
          return null; // caller will retry via legacy
        }
        console.error('[AddressAutocomplete] Place.fetchFields failed', err);
        return null;
      }
    },
    [ensureLegacyServices],
  );

  /* ─── SELECTION: legacy API path ─── */
  const selectViaLegacy = useCallback(
    (s: Suggestion): Promise<ResolvedAddress | null> => {
      return new Promise((resolve) => {
        const service = placesServiceRef.current;
        if (!service) {
          resolve(null);
          return;
        }
        service.getDetails(
          {
            placeId: s.placeId,
            sessionToken: sessionTokenRef.current ?? undefined,
            fields: ['place_id', 'formatted_address', 'name'],
          },
          (place, status) => {
            if (
              status !== google.maps.places.PlacesServiceStatus.OK ||
              !place
            ) {
              resolve(null);
              return;
            }
            const formatted =
              place.formatted_address ||
              [place.name, s.secondary].filter(Boolean).join(', ') ||
              s.primary;
            const display =
              place.name &&
              !formatted.toLowerCase().startsWith(place.name.toLowerCase())
                ? `${place.name}, ${formatted}`
                : formatted;
            resolve({
              placeId: place.place_id ?? s.placeId,
              formattedAddress: display,
            });
          },
        );
      });
    },
    [],
  );

  const handleSelect = useCallback(
    async (s: Suggestion) => {
      // Fallback resolution from the prediction strings — used when both
      // details paths fail. We still know placeId + the prediction text.
      const fromPrediction: ResolvedAddress = {
        placeId: s.placeId,
        formattedAddress: [s.primary, s.secondary].filter(Boolean).join(', '),
      };

      let resolved: ResolvedAddress | null = null;

      if (!useLegacyRef.current) {
        resolved = await selectViaNewApi(s);
        // selectViaNewApi may have flipped useLegacyRef to true if the
        // new API errored out — in that case retry via legacy below.
      }
      if (!resolved && useLegacyRef.current) {
        resolved = await selectViaLegacy(s);
      }
      if (!resolved) resolved = fromPrediction;

      setPicked(resolved);
      setQuery(resolved.formattedAddress);
      setOpen(false);
      setSuggestions([]);

      // Rotate the session token after each completed selection (per
      // Google billing best-practice). Works for both APIs.
      const lib = placesLibRef.current;
      if (lib) {
        sessionTokenRef.current = new lib.AutocompleteSessionToken();
      }
      onChange(resolved);
    },
    [onChange, selectViaNewApi, selectViaLegacy],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (!open || suggestions.length === 0) return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlight((h) => (h + 1) % suggestions.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlight((h) =>
          h <= 0 ? suggestions.length - 1 : h - 1,
        );
      } else if (e.key === 'Enter') {
        if (highlight >= 0 && highlight < suggestions.length) {
          e.preventDefault();
          void handleSelect(suggestions[highlight]);
        }
      } else if (e.key === 'Escape') {
        setOpen(false);
      }
    },
    [open, suggestions, highlight, handleSelect],
  );

  const showWarn = !renderPlain && !picked && query.trim().length >= 2;

  /* ─────────────── RENDER ─────────────── */

  return (
    <div className={styles.wrap} ref={wrapperRef}>
      {label && (
        <label className={styles.label} htmlFor={inputId}>
          {label}
        </label>
      )}
      <div
        className={`${styles.inputWrap} ${picked ? styles.inputWrapPicked : ''} ${
          showWarn ? styles.inputWrapWarn : ''
        }`}
      >
        <span className={styles.iconLeft} aria-hidden>
          <svg
            viewBox="0 0 24 24"
            width="14"
            height="14"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.7"
          >
            <path
              d="M12 21s-7-7.3-7-12a7 7 0 0114 0c0 4.7-7 12-7 12z"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <circle cx="12" cy="9.5" r="2.5" />
          </svg>
        </span>
        <input
          ref={inputRef}
          id={inputId}
          type="text"
          role="combobox"
          className={styles.input}
          placeholder={placeholder}
          value={query}
          onChange={(e) => handleQueryChange(e.target.value)}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          autoComplete="off"
          spellCheck={false}
          aria-expanded={open && suggestions.length > 0}
          aria-autocomplete="list"
          aria-controls={`${inputId}-listbox`}
        />
        {loading && (
          <span className={styles.spinner} aria-hidden>
            <svg
              viewBox="0 0 24 24"
              width="14"
              height="14"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" strokeLinecap="round" />
            </svg>
          </span>
        )}
        {picked && (
          <span className={styles.checkMark} aria-hidden>
            <svg
              viewBox="0 0 24 24"
              width="14"
              height="14"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
            >
              <path
                d="M5 12l5 5L20 7"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
        )}
      </div>

      {open && suggestions.length > 0 && (
        <ul
          className={styles.list}
          role="listbox"
          id={`${inputId}-listbox`}
        >
          {suggestions.map((s, i) => (
            <li key={s.placeId} role="option" aria-selected={i === highlight}>
              <button
                type="button"
                className={`${styles.option} ${
                  i === highlight ? styles.optionActive : ''
                }`}
                onMouseEnter={() => setHighlight(i)}
                onClick={() => void handleSelect(s)}
              >
                <span className={styles.optionIcon} aria-hidden>
                  <svg
                    viewBox="0 0 24 24"
                    width="12"
                    height="12"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                  >
                    <path
                      d="M12 21s-7-7.3-7-12a7 7 0 0114 0c0 4.7-7 12-7 12z"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <circle cx="12" cy="9.5" r="2.5" />
                  </svg>
                </span>
                <span className={styles.optionBody}>
                  <span className={styles.optionPrimary}>{s.primary}</span>
                  {s.secondary && (
                    <span className={styles.optionSecondary}>
                      {s.secondary}
                    </span>
                  )}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {renderPlain && (
        <span className={styles.fallbackNote}>{fallbackText}</span>
      )}
      {showWarn && warnText && (
        <span className={styles.warnNote}>{warnText}</span>
      )}
      {!hideConfirmation && picked && (
        <span className={styles.confirmation}>
          <svg
            viewBox="0 0 24 24"
            width="10"
            height="10"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            aria-hidden
          >
            <path
              d="M5 12l5 5L20 7"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span className={styles.confirmationText}>
            {picked.formattedAddress}
          </span>
        </span>
      )}
    </div>
  );
}
